/**
 * The governance wire shapes (`complan-governance/requirements.md` Requirements 1
 * to 10).
 *
 * ## The metric envelope is the module's centre
 *
 * {@link metricValueSchema} mirrors `@complan/domain`'s `MetricValue` exactly, and
 * every numeric figure on either dashboard travels inside one. That uniformity is
 * what lets the front end render R1.5's Viewer_Scope_Notice and R2.4's information
 * control from one component rather than per panel: a tile receives a
 * `MetricValue`, reads `viewerScoped` and `metricId`, and resolves the rest from
 * the Metric_Registry it shares with the server.
 *
 * `value` is nullable and `state` says why. R1.7's empty scope and R5.10's
 * Data_Incomplete are both "no number", and a client that received a bare `0`
 * could not tell either from a computed zero.
 *
 * ## Every request carries the Filter_Context, not a bespoke scope
 *
 * Both dashboard requests and all five roll-up requests embed
 * `initiativeFilterSchema`. R6.9 requires one filter selection to drive metrics,
 * visuals, drill-through and export identically, and reusing the grammar rather
 * than declaring a governance-specific one is what makes R1.1's "the same readable
 * scope and filter predicate the Calendar_View count is produced from" true of the
 * wire as well as of the SQL.
 *
 * ## Visual series are arrays, and the table renders the same array
 *
 * Each Governance_Visual carries its series as a plain array of points. R5.8
 * requires a Visual_Data_Table containing exactly the series the chart displays, and
 * Property 61 asserts it - both are only checkable because there is one array, not
 * a chart-shaped structure and a table-shaped one.
 */
import { z } from 'zod';
import { idSchema, initiativeRefSchema, planningDateSchema, planningYearSchema, reasonSchema, ruleIdSchema, timestampSchema, versionSchema, } from './primitives.js';
import { initiativeFilterSchema } from './filters.js';
import { metricStateSchema, metricUnitSchema, readinessStateSchema, watchoutSeveritySchema, watchoutStateSchema, } from './governance-enums.js';
import { dependencyFunctionSchema, fieldForceRoleSchema, initiativeStatusSchema } from './enums.js';
/* -------------------------------------------------------------------------- */
/* Metrics (Requirements 1, 2)                                                */
/* -------------------------------------------------------------------------- */
/**
 * One computed Governance_Metric (R1.4, R1.7, R1.9, R2.4).
 *
 * `metricId` is a plain bounded string rather than an enum of the 24 identifiers:
 * the authoritative list is `@complan/domain`'s `MetricId`, the server only ever
 * emits members of it, and restating all 24 here would be a second list to keep in
 * step for no gained safety on a response the client does not construct.
 */
export const metricValueSchema = z.object({
    metricId: z.string().min(1).max(80),
    state: metricStateSchema,
    /** Non-null exactly when `state` is `defined` - the no-`NaN` guarantee of R11.4. */
    value: z.number().finite().nullable(),
    /** R2.4: present for a share, null for a count or an hours total. */
    numerator: z.number().int().nonnegative().nullable(),
    denominator: z.number().int().nonnegative().nullable(),
    /** R1.4: set by the server from the Metric_Registry, never by the client. */
    viewerScoped: z.boolean(),
    /** R1.9: how many readable records the figure was computed over. */
    basisCount: z.number().int().nonnegative(),
});
/**
 * A Metric_Definition as the wire carries it (R2.4, R2.10).
 *
 * Served so an export can embed the definitions of the metrics it contains
 * (R2.10) and so a client that has not bundled the domain registry can still
 * render a tooltip. The screens themselves read the registry directly, which is
 * why this is a response shape and not a request one.
 */
export const metricDefinitionSchema = z.object({
    metricId: z.string().min(1).max(80),
    labelKey: z.string().min(1),
    numeratorKey: z.string().min(1),
    denominatorKey: z.string().min(1).nullable(),
    unit: metricUnitSchema,
    exclusionKeys: z.array(z.string().min(1)),
    viewerScoped: z.boolean(),
    criterion: z.string().min(1),
});
/* -------------------------------------------------------------------------- */
/* Shared request shape                                                       */
/* -------------------------------------------------------------------------- */
/**
 * What both dashboards and every roll-up are scoped by (R6.9, R3.12, R8.2).
 *
 * `cutOffDate` is explicit and optional rather than always "today": R8.2 requires
 * readiness at a past or future date to be reportable, and R3.12 requires the
 * dashboard to display the date it evaluated against. A server default of the
 * current date with the value echoed back is what lets the screen show it without
 * the client having to decide it.
 */
export const governanceScopeSchema = z.object({
    filter: initiativeFilterSchema,
    /** Defaults to the current system date on the server (R8.2). */
    cutOffDate: planningDateSchema.nullable().default(null),
});
/**
 * The scope echo every governance response carries (R3.12, R8.2, R2.4).
 *
 * `readAt` is R2.4's "the date and time the underlying data was read" and R3.12's
 * displayed timestamp; `readinessHorizonDays` travels because a Due count is
 * meaningless without it.
 */
export const governanceScopeEchoSchema = z.object({
    cutOffDate: planningDateSchema,
    readinessHorizonDays: z.number().int().min(1).max(90),
    readAt: timestampSchema,
    /** R1.9, R3.11: the readable record count the whole response was computed over. */
    readableInitiativeCount: z.number().int().nonnegative(),
});
/* -------------------------------------------------------------------------- */
/* Governance_Dashboard (Requirement 3)                                       */
/* -------------------------------------------------------------------------- */
/** One status and its readable count (R3.2). */
export const statusCountSchema = z.object({
    status: initiativeStatusSchema,
    count: z.number().int().nonnegative(),
});
/** R3.2: the status, finality and priority distribution panel. */
export const statusDistributionPanelSchema = z.object({
    byStatus: z.array(statusCountSchema),
    priorityShare: metricValueSchema,
    finalityShare: metricValueSchema,
});
/** R3.3, R5.4: the readiness panel and the funnel series it also feeds. */
export const readinessPanelSchema = z.object({
    allCompleteInitiatives: metricValueSchema,
    overdueInitiatives: metricValueSchema,
    dueInitiatives: metricValueSchema,
    missingInitiatives: metricValueSchema,
    notApplicableInitiatives: metricValueSchema,
    readinessShare: metricValueSchema,
    /** R5.4: the required-milestone partition across the Readable_Set. */
    funnel: z.array(z.object({ state: readinessStateSchema, milestoneCount: z.number().int().nonnegative() })),
});
/** One severity and its unresolved count (R3.4). */
export const watchoutSeverityCountSchema = z.object({
    severity: watchoutSeveritySchema,
    count: z.number().int().nonnegative(),
});
/** R3.4: post-approval change and Watchout panel. */
export const approvalWatchoutPanelSchema = z.object({
    changedAfterApprovalInitiatives: metricValueSchema,
    unresolvedWatchoutInitiatives: metricValueSchema,
    unresolvedWatchouts: metricValueSchema,
    bySeverity: z.array(watchoutSeverityCountSchema),
});
/** R3.5: dependency panel. */
export const dependencyPanelSchema = z.object({
    dteInvolvementInitiatives: metricValueSchema,
    cscInvolvementInitiatives: metricValueSchema,
    openDependencyInitiatives: metricValueSchema,
    overdueDependencies: metricValueSchema,
});
/**
 * One period's capacity state, as the Manpower services report it (R3.6, R5.2).
 *
 * `utilizationPct` is nullable and `bottleneckState` may be `data_incomplete`,
 * carried through from Manpower unchanged rather than substituted with zero
 * (R5.10, Assumption A15).
 */
export const capacityPeriodStateSchema = z.object({
    periodId: z.string().min(1),
    periodLabel: z.string().min(1),
    ffRole: fieldForceRoleSchema.nullable(),
    regionId: idSchema.nullable(),
    utilizationPct: z.number().finite().nullable(),
    demandHours: z.number().finite().nullable(),
    capacityHours: z.number().finite().nullable(),
    excessHours: z.number().finite().nullable(),
    bottleneckState: z.enum(['normal', 'warning', 'over_capacity', 'data_incomplete']),
});
/** R3.6: capacity panel, plus the threshold a reader needs to interpret it. */
export const capacityPanelSchema = z.object({
    overCapacityPeriods: metricValueSchema,
    excessDemandHours: metricValueSchema,
    capacityThresholdPct: z.number().finite(),
    overCapacityThresholdPct: z.number().finite(),
    periods: z.array(capacityPeriodStateSchema),
});
/** One validation rule and how many readable Initiatives leave it unmet (R3.7, R9.5). */
export const ruleExceptionCountSchema = z.object({
    ruleId: ruleIdSchema,
    tier: z.enum(['hard', 'governance', 'warning', 'information']),
    initiativeCount: z.number().int().nonnegative(),
});
/** R3.7: data-quality exception panel. */
export const dataQualityPanelSchema = z.object({
    governanceExceptionInitiatives: metricValueSchema,
    warningExceptionInitiatives: metricValueSchema,
    ownershipGapInitiatives: metricValueSchema,
    byRule: z.array(ruleExceptionCountSchema),
});
/** The complete Governance_Dashboard response (R3.2-R3.7, R3.12). */
export const governanceDashboardResponseSchema = z.object({
    scope: governanceScopeEchoSchema,
    statusDistribution: statusDistributionPanelSchema,
    readiness: readinessPanelSchema,
    approvalAndWatchouts: approvalWatchoutPanelSchema,
    dependencies: dependencyPanelSchema,
    capacity: capacityPanelSchema,
    dataQuality: dataQualityPanelSchema,
});
/* -------------------------------------------------------------------------- */
/* Management_Dashboard (Requirement 4) and visuals (Requirement 5)           */
/* -------------------------------------------------------------------------- */
/** One heatmap cell: a month, a Platform and the Demand_Hours in it (R5.1). */
export const heatmapCellSchema = z.object({
    month: z.number().int().min(1).max(12),
    platformId: idSchema,
    platformName: z.string().min(1),
    demandHours: z.number().finite().nullable(),
    dataIncomplete: z.boolean(),
});
/** One utilization point for one role and period (R5.2). */
export const utilizationPointSchema = z.object({
    periodId: z.string().min(1),
    periodLabel: z.string().min(1),
    ffRole: z.enum(['ade', 'bde', 'combined']),
    utilizationPct: z.number().finite().nullable(),
    dataIncomplete: z.boolean(),
});
/** One ranked demand-driving Initiative (R5.3). */
export const topDemandDriverSchema = z.object({
    initiativeId: idSchema,
    initiativeRef: initiativeRefSchema,
    title: z.string().min(1),
    demandHours: z.number().finite(),
});
/** One cell of the Region-by-Sales_Cycle capacity matrix (R5.5). */
export const capacityMatrixCellSchema = z.object({
    regionId: idSchema,
    regionName: z.string().min(1),
    salesCycleId: idSchema,
    salesCycleName: z.string().min(1),
    utilizationPct: z.number().finite().nullable(),
    bottleneckState: z.enum(['normal', 'warning', 'over_capacity', 'data_incomplete']),
});
/** One slice of the Initiative mix (R5.6, R4.5). */
export const initiativeMixSliceSchema = z.object({
    dimension: z.enum(['platform', 'initiative-type']),
    id: idSchema,
    name: z.string().min(1),
    initiativeCount: z.number().int().nonnegative(),
});
/** One planning year of the yearly trend (R4.3). */
export const yearlyTrendPointSchema = z.object({
    planningYear: planningYearSchema,
    /**
     * R4.3: a year holding no readable Initiative renders absent rather than zero,
     * so an empty year is not read as a collapse in activity. `present: false`
     * carries that, and the three metrics are null in that case.
     */
    present: z.boolean(),
    initiativeCount: metricValueSchema.nullable(),
    priorityShare: metricValueSchema.nullable(),
    finalityShare: metricValueSchema.nullable(),
});
/** R4.4: the risk panel. */
export const riskPanelSchema = z.object({
    overCapacityPeriods: metricValueSchema,
    overdueMilestoneInitiatives: metricValueSchema,
    highSeverityWatchoutInitiatives: metricValueSchema,
    changedAfterApprovalInitiatives: metricValueSchema,
});
/**
 * The Manpower half of the Management_Dashboard, omitted entirely for a principal
 * without `manpower:read` (R4.6).
 *
 * `null` with `omittedReason` rather than empty arrays and zeroed metrics, because
 * R4.6 requires the omission *stated*: a reader must be able to tell "you may not
 * see this" from "there is no demand".
 */
export const manpowerKpiBlockSchema = z.object({
    utilization: metricValueSchema,
    excessDemandHours: metricValueSchema,
    demandHoursTotal: metricValueSchema,
    workloadHeatmap: z.array(heatmapCellSchema),
    utilizationSeries: z.array(utilizationPointSchema),
    topDemandDrivers: z.array(topDemandDriverSchema),
    capacityMatrix: z.array(capacityMatrixCellSchema),
    /**
     * The two boundaries R5.2 requires the utilization visual to mark.
     *
     * Carried in the block rather than left to the client, because the threshold is
     * *configured* (the same value `capacityPanelSchema` echoes on the
     * Governance_Dashboard) and a screen that hard-coded it would draw a line that
     * disagreed with the Bottleneck_State the same response reports. The
     * Over_Capacity boundary is fixed at 100 and travels for symmetry, so a chart
     * reads both from one place.
     */
    capacityThresholdPct: z.number().finite(),
    overCapacityThresholdPct: z.number().finite(),
});
/** The complete Management_Dashboard response (R4.2-R4.6). */
export const managementDashboardResponseSchema = z.object({
    scope: governanceScopeEchoSchema,
    initiativeCount: metricValueSchema,
    priorityShare: metricValueSchema,
    finalityShare: metricValueSchema,
    readinessShare: metricValueSchema,
    yearlyTrend: z.array(yearlyTrendPointSchema),
    risk: riskPanelSchema,
    initiativeMix: z.array(initiativeMixSliceSchema),
    /** Null when the principal lacks `manpower:read` (R4.6). */
    manpower: manpowerKpiBlockSchema.nullable(),
    /** Set exactly when `manpower` is null, naming the missing capability (R4.6). */
    manpowerOmittedReason: z.enum(['missing_capability']).nullable(),
});
/* -------------------------------------------------------------------------- */
/* Watchouts (Requirement 7)                                                  */
/* -------------------------------------------------------------------------- */
/** The description length migration 26's `watchout_description_length` enforces. */
const watchoutDescriptionSchema = z.string().trim().min(1).max(1000);
/** One Watchout as the Detail_Panel and the roll-up read it (R7.1, R7.2, R7.9). */
export const watchoutSchema = z.object({
    id: idSchema,
    initiativeId: idSchema,
    watchoutCategoryId: idSchema,
    watchoutCategoryName: z.string().min(1).nullable(),
    description: watchoutDescriptionSchema,
    severity: watchoutSeveritySchema,
    ownerUserId: idSchema.nullable(),
    ownerDisplayName: z.string().min(1).nullable(),
    dueDate: planningDateSchema.nullable(),
    raisedByDisplayName: z.string().min(1).nullable(),
    raisedAt: timestampSchema,
    /** R7.2: derived on the server from `resolvedAt`, never stored. */
    state: watchoutStateSchema,
    resolvedByDisplayName: z.string().min(1).nullable(),
    resolvedAt: timestampSchema.nullable(),
    resolutionNote: z.string().trim().min(1).max(1000).nullable(),
});
/** `POST /api/initiatives/:id/watchouts` (R7.1, R7.4). */
export const createWatchoutRequestSchema = z.object({
    watchoutCategoryId: idSchema,
    description: watchoutDescriptionSchema,
    severity: watchoutSeveritySchema,
    ownerUserId: idSchema.nullable().default(null),
    dueDate: planningDateSchema.nullable().default(null),
});
/** `PUT /api/watchouts/:id` (R7.4, R7.6). */
export const updateWatchoutRequestSchema = createWatchoutRequestSchema;
/**
 * `POST /api/watchouts/:id/resolve` (R7.8).
 *
 * The note is optional because a resolution can be self-evident; migration 26's
 * `watchout_resolution_note_requires_resolution` only forbids the reverse - a note
 * without a resolution.
 */
export const resolveWatchoutRequestSchema = z.object({
    resolutionNote: z.string().trim().min(1).max(1000).nullable().default(null),
});
/** `GET /api/initiatives/:id/watchouts` (R7.9). */
export const watchoutListResponseSchema = z.object({ items: z.array(watchoutSchema) });
/* -------------------------------------------------------------------------- */
/* Cross-initiative roll-ups (Requirement 9)                                  */
/* -------------------------------------------------------------------------- */
/** Which roll-up is being requested (R9.1-R9.7). */
export const ROLLUP_KINDS = Object.freeze([
    'open-dependencies',
    'unresolved-watchouts',
    'change-history',
    'data-quality-exceptions',
    'ownership-gaps',
]);
/** See {@link ROLLUP_KINDS}. */
export const rollupKindSchema = z.enum(ROLLUP_KINDS);
/** The paging a roll-up request carries (R9.8). */
export const rollupPageSchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(50),
});
/** A roll-up request: the shared scope plus paging (R9.11). */
export const rollupRequestSchema = governanceScopeSchema.extend(rollupPageSchema.shape);
/** Fields every roll-up row carries, so a table component can render any of them (R9.10). */
const rollupRowBase = {
    initiativeId: idSchema,
    initiativeRef: initiativeRefSchema,
    title: z.string().min(1),
};
/** R9.1: one open dependency. */
export const openDependencyRowSchema = z.object({
    ...rollupRowBase,
    dependencyId: idSchema,
    dependencyFunction: dependencyFunctionSchema,
    description: z.string().nullable(),
    ownerText: z.string().nullable(),
    /** The activity date, or the start of an activity period. */
    dueDate: planningDateSchema.nullable(),
    /** The end of an activity period; `null` for a single activity date. */
    dueEndDate: planningDateSchema.nullable(),
    /** R9.1: whether the activity date (the end, for a period) precedes the cut-off date. */
    overdue: z.boolean(),
});
/** R9.2: one Unresolved_Watchout. */
export const unresolvedWatchoutRowSchema = z.object({
    ...rollupRowBase,
    watchoutId: idSchema,
    watchoutCategoryName: z.string().min(1).nullable(),
    severity: watchoutSeveritySchema,
    ownerDisplayName: z.string().min(1).nullable(),
    dueDate: planningDateSchema.nullable(),
});
/** R9.3: one Change_Event, restricted to the Readable_Set (R9.4). */
export const changeHistoryRowSchema = z.object({
    ...rollupRowBase,
    eventId: z.string().min(1),
    eventType: z.string().min(1),
    actorDisplayName: z.string().min(1).nullable(),
    occurredAt: timestampSchema,
    fieldPath: z.string().min(1).nullable(),
    reason: z.string().min(1).nullable(),
});
/** R9.5: one Data_Quality_Exception. */
export const dataQualityExceptionRowSchema = z.object({
    ...rollupRowBase,
    ruleId: ruleIdSchema,
    tier: z.enum(['hard', 'governance', 'warning', 'information']),
    messageKey: z.string().min(1),
    fieldPath: z.string().min(1).nullable(),
});
/** R9.7: one Ownership_Gap, naming every reason that applies. */
export const ownershipGapRowSchema = z.object({
    ...rollupRowBase,
    status: initiativeStatusSchema,
    reasons: z.array(z.enum(['owner_inactive', 'no_backup_owner_on_committed'])).min(1),
    ownerDisplayName: z.string().min(1).nullable(),
});
/**
 * A roll-up response (R9.8, R9.9).
 *
 * `totalCount` is the complete matching count, independent of `pageSize` (R9.8),
 * and R9.9 requires it to equal the corresponding dashboard metric - which
 * Property 60 asserts rather than assumes.
 */
export const rollupResponseSchema = z.object({
    kind: rollupKindSchema,
    scope: governanceScopeEchoSchema,
    page: z.number().int().min(1),
    pageSize: z.number().int().min(1),
    totalCount: z.number().int().nonnegative(),
    rows: z.array(z.union([
        openDependencyRowSchema,
        unresolvedWatchoutRowSchema,
        changeHistoryRowSchema,
        dataQualityExceptionRowSchema,
        ownershipGapRowSchema,
    ])),
});
/* -------------------------------------------------------------------------- */
/* Bulk_Edit (Requirement 10)                                                 */
/* -------------------------------------------------------------------------- */
/**
 * The five fields a Bulk_Edit may set (R10.1, R10.2).
 *
 * A `z.enum` over the domain's closed `LOW_RISK_FIELDS`, so the wire boundary is
 * the single place a client-supplied field name is narrowed - everything
 * downstream is typed `LowRiskField` and cannot be reached with a raw string.
 *
 * Watchout category assignment was removed from the allowlist by product decision;
 * `packages/domain/src/governance/bulk-edit.ts` records why at length.
 */
export const lowRiskFieldSchema = z.enum([
    'priorityId',
    'categoryId',
    'initiativeTypeId',
    'ownerUserId',
    'backupOwnerUserId',
]);
/**
 * One field assignment in a Bulk_Edit (R10.1).
 *
 * `value` is nullable so Backup Owner can be cleared; a null for a non-nullable
 * target field is rejected by the per-record hard validation R10.8 runs, not by
 * this schema, because whether a field accepts null is a rule rather than a shape.
 */
export const bulkEditAssignmentSchema = z.object({
    field: lowRiskFieldSchema,
    value: idSchema.nullable(),
});
/** `POST /api/bulk-edit/preview` (R10.4). */
export const bulkEditPreviewRequestSchema = z.object({
    initiativeIds: z.array(idSchema).min(1).max(1000),
    assignments: z.array(bulkEditAssignmentSchema).min(1),
});
/** One record a Bulk_Edit would change, with its before and after values (R10.4). */
export const bulkEditPreviewRowSchema = z.object({
    initiativeId: idSchema,
    initiativeRef: initiativeRefSchema,
    title: z.string().min(1),
    /** R10.9: the version the preview observed, echoed back on apply. */
    version: versionSchema,
    changes: z.array(z.object({
        field: lowRiskFieldSchema,
        beforeId: idSchema.nullable(),
        beforeLabel: z.string().min(1).nullable(),
        afterId: idSchema.nullable(),
        afterLabel: z.string().min(1).nullable(),
    })),
    /** R10.12: whether applying would set this record's changed-after-approval flag. */
    willFlagChangedAfterApproval: z.boolean(),
});
/** One record the operation would reject, and why (R10.4). */
export const bulkEditRejectionSchema = z.object({
    initiativeId: idSchema,
    /**
     * Absent for `not_readable`: R10.5 forbids disclosing whether an Initiative the
     * requester may not read exists, so no reference or title travels for that case.
     */
    initiativeRef: initiativeRefSchema.nullable(),
    reason: z.enum([
        'not_readable',
        'cancelled',
        'deactivated_value',
        'validation_failed',
        'version_conflict',
    ]),
    ruleIds: z.array(ruleIdSchema),
});
/** `POST /api/bulk-edit/preview` response (R10.4, R10.6). */
export const bulkEditPreviewResponseSchema = z.object({
    /** R10.15: echoed on apply, so an unpreviewed apply is rejectable. */
    previewToken: z.string().min(1),
    assignments: z.array(bulkEditAssignmentSchema),
    changedCount: z.number().int().nonnegative(),
    /** R10.4: records whose value already matches, so nothing would change. */
    unchangedCount: z.number().int().nonnegative(),
    rows: z.array(bulkEditPreviewRowSchema),
    rejections: z.array(bulkEditRejectionSchema),
    /** R10.6: the configured cap and the requested count, so a rejection can state both. */
    maxRecords: z.number().int().positive(),
    requestedCount: z.number().int().nonnegative(),
});
/**
 * `POST /api/bulk-edit/apply` (R10.7, R10.9, R10.11, R10.15).
 *
 * Carries the observed versions so R10.9's conflict check needs no server-side
 * preview cache, and the `previewToken` so R10.15's "not previewed in the same
 * interaction" is refusable.
 */
export const bulkEditApplyRequestSchema = z.object({
    previewToken: z.string().min(1),
    assignments: z.array(bulkEditAssignmentSchema).min(1),
    expectedVersions: z
        .array(z.object({ initiativeId: idSchema, version: versionSchema }))
        .min(1),
    reason: reasonSchema,
});
/** `POST /api/bulk-edit/apply` response (R10.10, R10.11). */
export const bulkEditApplyResponseSchema = z.object({
    bulkEditRunId: z.string().min(1),
    appliedAt: timestampSchema,
    affectedCount: z.number().int().nonnegative(),
    changedInitiativeIds: z.array(idSchema),
    flaggedChangedAfterApprovalIds: z.array(idSchema),
});
//# sourceMappingURL=governance.js.map